<script setup>
// FLOW-06 (SPEC S5 e a emenda do Gate 2; Task 6.0, depois 6.4) — a seção Flows da aba PowerFlow: a
// lista dos flows da conta, que é nova (até aqui o flow só aparecia dentro do agente). Recebe o
// `payload` do `GET /api/v1/accounts/:id/powerflow/flows` como veio, um item por nome: a publicada
// mais nova, o rascunho, quando há, e o resumo do PowerTools (a última execução, as execuções e a
// taxa de erro das últimas 24 h). Só mostra: quem a usa busca, executa e navega.
//
// **O gatilho** (FLOW-03a, SPEC S7; Task 7.3; FLOW-03b, Task 3.8, P14): embaixo de cada flow
// publicado, uma linha com o gatilho da versão publicada (`GatilhosDaLinhaDoFlow`): a agenda ou o
// webhook, com o liga e pausa, a próxima batida e as contagens do dia; sem nenhum dos dois, o
// Agente, que chama o flow como tool. O rascunho nunca publicado não tem linha. Chega por fora
// (`gatilhoPorFlow`, o `GET /powerflow/gatilhos` pelo nome), e nulo enquanto não chegou ou se a
// lista falhou: aí a linha não diz nada sobre gatilho.
//
// **O que cada um vê** (S5, Gate 1 Q4): "Executar agora" é de quem vê a aba, e só no flow com versão
// publicada (rascunho só roda no Testar, S2). "Novo flow" e "Abrir no editor" são de quem monta flow
// (`podeEditar`, a régua do `useMontaFlow`): o editor não tem modo de leitura e devolve quem não
// monta. O que a pessoa não pode fazer não aparece como botão. ACESSO-01 (L11b): com a chave
// ACESSO_GRANULAR ligada, "Executar agora" e o liga e pausa do gatilho são de quem tem o
// `powerflow:run` (`executa`, abaixo).
//
// **A busca** (pedida pelo Murilo ao aprovar o mockup, 25/10) é a da barra de filtros padrão
// (`FilterBar`, como na lista de agentes) e filtra aqui mesmo, sem ir ao servidor: pelo nome técnico,
// pelo título legível e pela descrição, sem diferença de maiúscula e acento (o `normalizar` da busca
// do agente, o mesmo do cofre). O termo é um `v-model:busca` opcional: sem ele, fica no componente.
//
// **O desenho** segue o cofre da conta (`SegredosDaConta.vue`), a outra lista da aba: o painel com
// as linhas, o ícone da forma da aba Ferramentas do agente (`IconeDaTool`), o título legível do nome
// técnico (`humanizar`, a regra das cartas) e o estado em glifo colorido com texto neutro. O glifo,
// o nome do estado e o "há quanto" são os das outras telas da aba (`useExecucaoNaTela`).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAcesso } from 'dashboard/composables/useAcesso';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import IconeDaTool from '../settings/powerAgents/sections/IconeDaTool.vue';
import { normalizar } from '../settings/powerAgents/busca';
import SeloDoFlow from '../settings/powerTools/flow/SeloDoFlow.vue';
import { humanizar } from '../settings/powerTools/usoDoSegredo';
import GatilhosDaLinhaDoFlow from './GatilhosDaLinhaDoFlow.vue';
import { GLIFOS_DOS_ESTADOS, useExecucaoNaTela } from './useExecucaoNaTela';

const props = defineProps({
  // O `payload` da rota, como veio: `[{nome, descricao, forma, publicada, rascunho, resumo}]`.
  flows: { type: Array, default: () => [] },
  // O `pode_editar` da mesma resposta: o super-admin, ou o gestor com POWERFLOW_ADMIN_MONTA_FLOW.
  podeEditar: { type: Boolean, default: false },
  carregando: { type: Boolean, default: false },
  // `{[nome do flow]: gatilho publicado}`, ou nulo enquanto não chegou (ou se falhou).
  gatilhoPorFlow: { type: Object, default: null },
});

const emit = defineEmits([
  'executar',
  'abrir',
  'novo',
  'alternarGatilho',
  'abrirGatilho',
]);

const busca = defineModel('busca', { type: String, default: '' });

const { t } = useI18n();
const { idioma, rotulosDosEstados, haQuanto } = useExecucaoNaTela();

// ACESSO-01 (L11b): "Executar agora" roda a publicada (`powerflow_execucoes#create`), e o liga e pausa
// muda o gatilho dela (`powerflow_gatilhos#update`). Com a chave ACESSO_GRANULAR ligada, o servidor
// pede o `powerflow:run` aos dois; desligada, são de quem vê a aba, como hoje.
const { decide } = useAcesso();
const executa = computed(() => decide('powerflow:run', () => true));

// As colunas, iguais no cabeçalho, nas linhas e no esqueleto. As linhas são grades separadas, então
// toda coluna, menos a do flow, tem largura fixa; a das ações encolhe quando não há o lápis.
const GRADE_COM_EDITOR = 'xl:grid-cols-[minmax(0,1fr)_10rem_9rem_12rem]';
const GRADE_SEM_EDITOR = 'xl:grid-cols-[minmax(0,1fr)_10rem_9rem_10rem]';

// O flow que nunca rodou (ou cuja última execução saiu da retenção) não tem estado: um traço neutro.
const SEM_EXECUCAO = 'i-lucide-minus text-n-slate-10';

const ultimaDe = flow => flow.resumo?.ultima || null;

const glifoDa = execucao =>
  execucao ? GLIFOS_DOS_ESTADOS[execucao.estado] || SEM_EXECUCAO : SEM_EXECUCAO;

// A execução que ainda roda não tem `terminada_em`: o estado já diz que é agora.
const rotuloDa = execucao =>
  execucao
    ? rotulosDosEstados.value[execucao.estado]
    : t('POWER_TOOLS.POWERFLOW.FLOWS.NO_EXECUTIONS');

const formatoDoNumero = computed(() => new Intl.NumberFormat(idioma.value));
const formatoDaTaxa = computed(
  () =>
    new Intl.NumberFormat(idioma.value, {
      style: 'percent',
      maximumFractionDigits: 1,
    })
);

const execucoesEm24h = flow => {
  const n = flow.resumo?.execucoes_24h || 0;
  return t(
    'POWER_TOOLS.POWERFLOW.FLOWS.EXECUTIONS_24H',
    { count: formatoDoNumero.value.format(n) },
    n
  );
};

// Sem execução em 24 h, a taxa vem nula: não há o que dizer.
const taxaDe = flow => {
  const taxa = flow.resumo?.taxa_de_erro_24h;
  if (taxa === null || taxa === undefined || !flow.resumo?.execucoes_24h) {
    return '';
  }
  return t('POWER_TOOLS.POWERFLOW.FLOWS.ERROR_RATE', {
    taxa: formatoDaTaxa.value.format(taxa),
  });
};

// -- Busca
const termo = computed(() => normalizar(busca.value).trim());
const visiveis = computed(() =>
  termo.value
    ? props.flows.filter(flow =>
        [flow.nome, humanizar(flow.nome), flow.descricao].some(campo =>
          normalizar(campo).includes(termo.value)
        )
      )
    : props.flows
);
const contagem = computed(() => {
  const total = props.flows.length;
  return termo.value
    ? t(
        'POWER_TOOLS.POWERFLOW.FLOWS.COUNT_FILTERED',
        { visiveis: visiveis.value.length, count: total },
        total
      )
    : t('POWER_TOOLS.POWERFLOW.FLOWS.COUNT', { count: total }, total);
});
</script>

<template>
  <section class="flex flex-col gap-4" data-flows-da-conta>
    <header class="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div class="flex flex-col gap-1 max-w-3xl">
        <h2 class="mb-0 text-heading-1 text-n-slate-12">
          {{ t('POWER_TOOLS.POWERFLOW.FLOWS.TITLE') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('POWER_TOOLS.POWERFLOW.FLOWS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="podeEditar"
        size="sm"
        icon="i-lucide-plus"
        class="shrink-0"
        data-novo-flow
        :label="t('POWER_TOOLS.POWERFLOW.FLOWS.NEW')"
        @click="emit('novo')"
      />
    </header>

    <!-- Carregando: o esqueleto tem a forma das linhas, e o leitor de tela ouve o texto. -->
    <div
      v-if="carregando"
      role="status"
      class="border divide-y rounded-pz border-n-weak divide-n-weak bg-n-card shadow-pz"
      data-flows-carregando
    >
      <span class="sr-only">{{
        t('POWER_TOOLS.POWERFLOW.FLOWS.LOADING')
      }}</span>
      <div
        v-for="linha in 3"
        :key="linha"
        aria-hidden="true"
        class="flex flex-wrap items-center gap-x-5 gap-y-3 py-4 pe-4 ps-5 motion-safe:animate-pulse xl:grid"
        :class="podeEditar ? GRADE_COM_EDITOR : GRADE_SEM_EDITOR"
      >
        <div class="flex items-center w-full min-w-0 gap-3">
          <span class="size-[30px] shrink-0 rounded-[7px] bg-n-alpha-2" />
          <span class="flex flex-col flex-1 gap-2">
            <span class="w-40 h-3 rounded bg-n-alpha-2" />
            <span class="h-2.5 rounded w-72 max-w-full bg-n-alpha-2" />
          </span>
        </div>
        <span class="w-24 h-3 rounded bg-n-alpha-2" />
        <span class="w-20 h-3 rounded bg-n-alpha-2" />
        <span class="h-8 rounded-lg w-36 ms-auto bg-n-alpha-2" />
      </div>
    </div>

    <template v-else-if="flows.length">
      <!-- A busca da barra de filtros padrão, só com a busca, como a lista de agentes: a lista é
           filtrada aqui mesmo, então o debounce fica em 0. A contagem vai no lugar das ações. -->
      <FilterBar
        data-busca-de-flow
        :model-value="{ q: busca }"
        :search="{
          placeholder: t('POWER_TOOLS.POWERFLOW.FLOWS.SEARCH_PLACEHOLDER'),
        }"
        :debounce="0"
        @update:model-value="valor => (busca = valor.q)"
      >
        <template #actions>
          <span
            class="text-xs font-medium text-n-slate-11"
            aria-live="polite"
            data-contagem
          >
            {{ contagem }}
          </span>
        </template>
      </FilterBar>

      <div
        v-if="!visiveis.length"
        class="flex flex-col items-center gap-1.5 px-4 py-7 text-center border rounded-pz border-n-weak bg-n-card shadow-pz"
        data-busca-sem-resultado
      >
        <span
          class="i-lucide-search size-4 text-n-slate-10"
          aria-hidden="true"
        />
        <p class="mb-0 text-sm text-n-slate-11">
          {{
            t('POWER_TOOLS.POWERFLOW.FLOWS.SEARCH_EMPTY', {
              termo: busca.trim(),
            })
          }}
        </p>
      </div>

      <div
        v-else
        class="border rounded-pz border-n-weak bg-n-card shadow-pz"
        data-flows
      >
        <!-- O cabeçalho das colunas é só para o olho; cada célula repete o nome ao leitor de tela. -->
        <div
          aria-hidden="true"
          class="hidden py-2.5 pe-4 ps-5 border-b gap-x-5 border-n-weak text-xs font-medium text-n-slate-11 xl:grid"
          :class="podeEditar ? GRADE_COM_EDITOR : GRADE_SEM_EDITOR"
        >
          <span>{{ t('POWER_TOOLS.POWERFLOW.FLOWS.COLUMNS.FLOW') }}</span>
          <span>{{
            t('POWER_TOOLS.POWERFLOW.FLOWS.COLUMNS.LAST_EXECUTION')
          }}</span>
          <span>{{ t('POWER_TOOLS.POWERFLOW.FLOWS.COLUMNS.LAST_24H') }}</span>
        </div>

        <ul class="p-0 m-0 list-none divide-y divide-n-weak">
          <li
            v-for="flow in visiveis"
            :key="flow.nome"
            class="flex flex-wrap items-center gap-x-5 gap-y-3 py-3.5 pe-4 ps-5 xl:grid"
            :class="podeEditar ? GRADE_COM_EDITOR : GRADE_SEM_EDITOR"
            data-flow
          >
            <div class="flex items-start w-full min-w-0 gap-3">
              <IconeDaTool
                :flow="flow.forma === 'flow'"
                :http="flow.forma === 'ferramenta_http'"
                class="mt-0.5"
              />
              <div class="flex flex-col min-w-0 gap-0.5">
                <h3
                  class="mb-0 text-sm font-medium truncate text-n-slate-12"
                  data-titulo
                >
                  {{ humanizar(flow.nome) }}
                </h3>
                <p
                  v-if="flow.descricao"
                  class="mb-0 text-xs text-n-slate-11 line-clamp-2"
                >
                  {{ flow.descricao }}
                </p>
                <!-- O nome técnico é o que o agente, a API e o MCP chamam; a versão é a que roda. -->
                <span
                  class="flex flex-wrap items-center mt-1 gap-x-1.5 gap-y-0.5 text-xs text-n-slate-11"
                  data-versao
                >
                  <span class="font-mono truncate">{{ flow.nome }}</span>
                  <span
                    class="rounded-full size-[3px] bg-n-slate-10"
                    aria-hidden="true"
                  />
                  <SeloDoFlow v-if="flow.publicada" cor="bg-n-teal-9">
                    {{
                      t('POWER_TOOLS.POWERFLOW.FLOWS.PUBLISHED', {
                        versao: flow.publicada.versao,
                      })
                    }}
                  </SeloDoFlow>
                  <SeloDoFlow
                    v-else-if="flow.rascunho"
                    icone="i-lucide-circle-dashed"
                    cor="text-n-amber-11"
                  >
                    {{
                      t('POWER_TOOLS.POWERFLOW.FLOWS.DRAFT_ONLY', {
                        versao: flow.rascunho.versao,
                      })
                    }}
                  </SeloDoFlow>
                  <template v-if="flow.publicada && flow.rascunho">
                    <span
                      class="rounded-full size-[3px] bg-n-slate-10"
                      aria-hidden="true"
                    />
                    <span class="whitespace-nowrap">
                      {{
                        t('POWER_TOOLS.POWERFLOW.FLOWS.NEWER_DRAFT', {
                          versao: flow.rascunho.versao,
                        })
                      }}
                    </span>
                  </template>
                </span>
              </div>
            </div>

            <div
              class="flex flex-col min-w-[10rem] gap-0.5 ms-[42px] xl:min-w-0 xl:ms-0"
              data-ultima-execucao
            >
              <span class="sr-only">
                {{ t('POWER_TOOLS.POWERFLOW.FLOWS.COLUMNS.LAST_EXECUTION') }}
              </span>
              <span class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <span
                  class="size-3.5 shrink-0"
                  :class="glifoDa(ultimaDe(flow))"
                  aria-hidden="true"
                />
                {{ rotuloDa(ultimaDe(flow)) }}
              </span>
              <time
                v-if="ultimaDe(flow)?.terminada_em"
                :datetime="ultimaDe(flow).terminada_em"
                class="text-xs ps-5 text-n-slate-11"
              >
                {{ haQuanto(ultimaDe(flow).terminada_em) }}
              </time>
            </div>

            <div class="flex flex-col gap-0.5 tabular-nums" data-24h>
              <span class="sr-only">
                {{ t('POWER_TOOLS.POWERFLOW.FLOWS.COLUMNS.LAST_24H') }}
              </span>
              <span class="text-sm text-n-slate-12">
                {{ execucoesEm24h(flow) }}
              </span>
              <span v-if="taxaDe(flow)" class="text-xs text-n-slate-11">
                {{ taxaDe(flow) }}
              </span>
            </div>

            <div class="flex items-center justify-end gap-1.5 ms-auto xl:ms-0">
              <Button
                v-if="flow.publicada && executa"
                size="sm"
                variant="faded"
                color="slate"
                icon="i-lucide-play"
                data-executar
                :label="t('POWER_TOOLS.POWERFLOW.FLOWS.EXECUTE_NOW')"
                @click="emit('executar', flow)"
              />
              <span
                v-else-if="!flow.publicada && podeEditar"
                class="text-xs text-n-slate-11"
              >
                {{ t('POWER_TOOLS.POWERFLOW.FLOWS.PUBLISH_TO_EXECUTE') }}
              </span>
              <Button
                v-if="podeEditar"
                v-tooltip.top="t('POWER_TOOLS.POWERFLOW.FLOWS.OPEN_IN_EDITOR')"
                size="sm"
                variant="ghost"
                color="slate"
                icon="i-lucide-square-pen"
                data-abrir
                :aria-label="t('POWER_TOOLS.POWERFLOW.FLOWS.OPEN_IN_EDITOR')"
                @click="emit('abrir', flow)"
              />
            </div>

            <GatilhosDaLinhaDoFlow
              v-if="gatilhoPorFlow && flow.publicada"
              class="w-full ps-[42px] xl:col-span-4"
              :gatilho="gatilhoPorFlow[flow.nome] || null"
              :pode-operar="executa"
              @alternar="
                (gatilho, ligado) => emit('alternarGatilho', gatilho, ligado)
              "
              @abrir="gatilho => emit('abrirGatilho', gatilho)"
            />
          </li>
        </ul>
      </div>
    </template>

    <div
      v-else
      class="flex flex-col items-center gap-3 px-6 py-12 text-center border rounded-pz border-n-weak bg-n-card shadow-pz"
      data-flows-vazio
    >
      <span
        class="grid size-10 place-items-center rounded-xl bg-n-alpha-black2 text-n-slate-11"
      >
        <span class="i-lucide-workflow size-4" aria-hidden="true" />
      </span>
      <h3 class="mb-0 text-heading-2 text-n-slate-12">
        {{ t('POWER_TOOLS.POWERFLOW.FLOWS.EMPTY_TITLE') }}
      </h3>
      <p class="mb-0 text-sm max-w-prose text-n-slate-11">
        {{ t('POWER_TOOLS.POWERFLOW.FLOWS.EMPTY_DESCRIPTION') }}
      </p>
      <Button
        v-if="podeEditar"
        icon="i-lucide-plus"
        data-novo-flow
        :label="t('POWER_TOOLS.POWERFLOW.FLOWS.NEW')"
        @click="emit('novo')"
      />
    </div>
  </section>
</template>
